import { type ComponentType, type JSX, lazy, useEffect, useState } from 'react';

/**
 * A route's screen, loaded as its own chunk (docs/portal-latency-audit.html,
 * eighth pass: the three portals shipped every screen in one entry chunk).
 *
 * `React.lazy` alone has two costs this wrapper removes.
 *
 *  - It suspends on EVERY first mount of a lazy component, even when the chunk
 *    is already in memory: the factory's promise settles a microtask later, so
 *    a screen that was fetched in the background still flashes the fallback
 *    once. Once `preload()` has resolved, this renders the loaded component
 *    directly and nothing suspends.
 *  - The choice is made ONCE per mount (`useState`), never per render: a
 *    screen that mounted through the lazy path and then re-rendered as the
 *    plain component would change element type and remount, losing its state.
 *
 * `preload()` is idempotent and returns the same promise to every caller; a
 * failed fetch is forgotten, so the next caller tries again rather than
 * inheriting the failure.
 */
export interface LazyScreen<P extends object> {
  (props: P): JSX.Element;
  preload(): Promise<unknown>;
}

export function lazyScreen<P extends object>(load: () => Promise<ComponentType<P>>): LazyScreen<P> {
  let loaded: ComponentType<P> | null = null;
  let pending: Promise<ComponentType<P>> | null = null;

  const preload = (): Promise<ComponentType<P>> => {
    pending ??= load().then(
      (component) => {
        loaded = component;
        return component;
      },
      (error: unknown) => {
        pending = null;
        throw error;
      },
    );
    return pending;
  };

  const Lazy = lazy(async () => ({ default: await preload() }));

  function Screen(props: P): JSX.Element {
    const [Ready] = useState(() => loaded);
    return Ready ? <Ready {...props} /> : <Lazy {...props} />;
  }
  return Object.assign(Screen, { preload });
}

/**
 * Fetch these screens' chunks once the page has painted and gone idle, so a
 * later navigation finds them in memory and renders without a fallback. Every
 * chunk is immutable and cached, so this spends the bytes the single entry
 * chunk used to spend - after the first screen instead of before it - and it
 * also shortens the window in which a tab left open across a deploy could ask
 * for a chunk that deploy removed.
 *
 * Pass a module-level list: a new array on every render would restart it.
 * In order, one after another, so the first-named - the likely next screen -
 * is the first to arrive and the rest do not compete with it.
 */
export function usePreloadWhenIdle(screens: readonly { preload(): Promise<unknown> }[]): void {
  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      for (const screen of screens) {
        if (cancelled) return;
        try {
          await screen.preload();
        } catch {
          // A background fetch that failed is not an error anybody can act on;
          // the route's own mount fetches again and reports it there.
        }
      }
    };
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(() => void run(), { timeout: 2000 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(handle);
      };
    }
    const handle = window.setTimeout(() => void run(), 1000);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [screens]);
}
